import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { DIAG } from "@/constants/testIds";
import { Zap, Thermometer, Gauge, AlertTriangle } from "lucide-react";

const DTC_CODES = [
  { code: "P0420", severity: "med", desc: "Catalyst Efficiency · Bank 1" },
  { code: "P0301", severity: "high", desc: "Cylinder 1 Misfire Detected" },
  { code: "P0128", severity: "low", desc: "Coolant Temp Below Threshold" },
  { code: "B1315", severity: "low", desc: "Battery Voltage Low" },
];

const SENSOR_STREAM = [
  { label: "RPM", val: 2840, unit: "rpm", Icon: Gauge },
  { label: "COOLANT", val: 92, unit: "°C", Icon: Thermometer },
  { label: "MAF", val: 12.4, unit: "g/s", Icon: Zap },
  { label: "O2 B1S1", val: 0.82, unit: "V", Icon: Zap },
];

const sevColor = (s) =>
  s === "high" ? "text-mclaren border-mclaren/40 bg-mclaren/5"
  : s === "med" ? "text-sunset border-sunset/40 bg-sunset/5"
  : "text-cyan-electric border-cyan-electric/30 bg-cyan-electric/5";

export const DiagnosticMockUI = () => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 900);
    return () => clearInterval(id);
  }, []);

  const jitter = (base, amp) => (base + Math.sin(tick * 1.3) * amp).toFixed(1);

  return (
    <section
      data-testid={DIAG.section}
      className="relative bg-void py-24 lg:py-40 border-t border-white/5"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <span className="chip-brand">/ 04 · console</span>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl font-semibold text-white tracking-tighter leading-[0.95]">
              A cockpit built for<br />signal, not noise.
            </h2>
            <p className="mt-6 text-white/60 text-base font-light leading-relaxed">
              Every value on this console is a real OBD-II datapoint, styled the
              way an engineer would want it. Legible, tight, and free of the
              guardrail chrome that hides what your car is telling you.
            </p>
            <button
              data-testid={DIAG.runBtn}
              className="mt-10 group inline-flex items-center gap-3 h-11 px-5 border border-cyan-electric/50 text-cyan-electric hover:bg-cyan-electric hover:text-void font-mono-tech text-[11px] uppercase tracking-[0.2em] transition-all"
            >
              <span className="h-1.5 w-1.5 bg-cyan-electric group-hover:bg-void animate-pulse-dot" />
              Run full scan
            </button>
          </div>

          {/* Console */}
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="relative border border-white/10 bg-obsidian/80 backdrop-blur-xl overflow-hidden hologram-glow"
            >
              {/* Header bar */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-black/40">
                <div className="flex items-center gap-4">
                  <div className="flex gap-1.5">
                    <span className="h-2 w-2 bg-mclaren rounded-full" />
                    <span className="h-2 w-2 bg-sunset rounded-full" />
                    <span className="h-2 w-2 bg-cyan-electric rounded-full" />
                  </div>
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40">
                    mymech://console · vin·1HGCM82633A123456
                  </span>
                </div>
                <span className="font-mono-tech text-[10px] text-cyan-electric flex items-center gap-2">
                  <span className="h-1.5 w-1.5 bg-cyan-electric rounded-full animate-pulse-dot" />
                  LIVE · 32ms
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2">
                {/* Left: sensor stream + interactive 3D vehicle stage */}
                <div className="p-6 lg:p-8 border-b md:border-b-0 md:border-r border-white/10 relative scan-line overflow-hidden">
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40 mb-6">
                    ▸ Live telemetry
                  </div>
                  <div className="grid grid-cols-2 gap-5">
                    {SENSOR_STREAM.map((s) => (
                      <div key={s.label} className="border border-white/10 p-4 bg-black/30">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40">
                            {s.label}
                          </span>
                          <s.Icon className="h-3.5 w-3.5 text-cyan-electric" strokeWidth={1.5} />
                        </div>
                        <div className="font-display text-3xl font-semibold text-white tracking-tighter">
                          {jitter(s.val, s.val * 0.02)}
                          <span className="ml-1 text-xs text-white/40 font-mono-tech">{s.unit}</span>
                        </div>
                        {/* Mini bar */}
                        <div className="mt-3 h-0.5 w-full bg-white/10">
                          <div
                            className="h-full bg-cyan-electric transition-all duration-700"
                            style={{ width: `${40 + (tick * 7) % 55}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Vehicle video - clean, no overlays */}
                  <div
                    data-testid="diag-3d-stage"
                    className="mt-6 relative border border-white/10 bg-black overflow-hidden"
                  >
                    <video
                      poster="/vehicle-3d.jpg"
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="auto"
                      className="block w-full h-auto"
                    >
                      <source src="/vehicle-3d.webm" type="video/webm" />
                      <source src="/vehicle-3d.mp4" type="video/mp4" />
                    </video>
                  </div>
                </div>

                {/* Right: DTC codes */}
                <div className="p-6 lg:p-8">
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40 mb-6 flex items-center justify-between">
                    <span>▸ Fault codes · {DTC_CODES.length} found</span>
                    <AlertTriangle className="h-3.5 w-3.5 text-mclaren" />
                  </div>
                  <ul className="space-y-3">
                    {DTC_CODES.map((c) => (
                      <li
                        key={c.code}
                        className={`border ${sevColor(c.severity)} p-4 flex items-center justify-between transition-all hover:translate-x-1`}
                      >
                        <div>
                          <div className="font-mono-tech text-lg font-semibold">{c.code}</div>
                          <div className="font-mono-tech text-[11px] uppercase tracking-widest text-white/60 mt-1">
                            {c.desc}
                          </div>
                        </div>
                        <span className="font-mono-tech text-[10px] uppercase tracking-widest opacity-70">
                          {c.severity === "high" ? "CRIT" : c.severity === "med" ? "WARN" : "INFO"}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                    <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40">
                      Confidence
                    </span>
                    <span className="font-display text-xl text-cyan-electric font-semibold tracking-tight">
                      94.2%
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DiagnosticMockUI;
